import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, ArrowLeft, Bus, CheckCircle2, XCircle, Camera, ImageIcon,
  Calculator, FileText, Clock, Users, UserPlus, Check, Trash2,
  Search, ChevronLeft, ChevronRight, Plus,
} from "lucide-react";

const STATUSES = ["Not Started", "Active", "In Progress", "Pending", "Completed", "Others"];
const BUS_TYPES = ["40ft Transit", "60ft Articulated", "Cutaway", "Double Decker", "School Bus", "Coach", "Other"];
const ENERGY_SOURCES = ["Diesel", "CNG", "Electric", "Hybrid", "Hydrogen", "Gasoline", "Other"];
const emptyBus = () => ({ bus_number: "", vin: "", bus_type: "", manufacturer: "", energy_source: "" });

function fmtDuration(sec) {
  sec = sec || 0;
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60);
  return `${h}h ${m}m`;
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className={`font-heading text-2xl font-black ${accent || "text-slate-900"}`}>{value}</div>
      <div className="overline text-slate-500">{label}</div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const [project, setProject] = useState(null);
  const [techs, setTechs] = useState([]);
  const [returned, setReturned] = useState("");
  const [busy, setBusy] = useState(false);
  const [vault, setVault] = useState(null);
  const [serialEdits, setSerialEdits] = useState({});
  const [delOpen, setDelOpen] = useState(false);
  const [busSearch, setBusSearch] = useState("");
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [newBuses, setNewBuses] = useState([emptyBus()]);

  const load = useCallback(() => {
    api.get(`/projects/${id}`).then((r) => {
      setProject(r.data);
      setReturned(String(r.data.parts_returned || ""));
    });
  }, [id]);

  useEffect(() => {
    load();
    if (["project_manager", "super_admin"].includes(user.role)) {
      api.get("/technicians").then((r) => setTechs(r.data)).catch(() => {});
    }
  }, [load, user.role]);

  if (!project)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const installs = project.installations || [];
  const installedCount = installs.filter((i) => i.status === "Approved").length;
  const totalTime = installs.reduce((s, i) => s + (i.elapsed_seconds || 0), 0);
  const isAdmin = ["admin", "super_admin"].includes(user.role);
  const isPM = ["project_manager", "super_admin"].includes(user.role);
  const isTech = user.role === "technician";
  const assignedIds = project.technician_ids || [];

  const q = busSearch.trim().toLowerCase();
  const filteredInstalls = q
    ? installs.filter((i) =>
        (i.bus_number || "").toLowerCase().includes(q) ||
        (i.vin || i.expected_bus_serial || "").toLowerCase().includes(q))
    : installs;
  const totalPages = Math.max(1, Math.ceil(filteredInstalls.length / perPage));
  const curPage = Math.min(page, totalPages);
  const pagedInstalls = filteredInstalls.slice((curPage - 1) * perPage, curPage * perPage);

  const setProjectField = async (patch) => {
    setBusy(true);
    try { await api.patch(`/projects/${id}`, patch); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleProjectTech = async (tid) => {
    const next = assignedIds.includes(tid) ? assignedIds.filter((x) => x !== tid) : [...assignedIds, tid];
    await setProjectField({ technician_ids: next });
    toast.success(t("projectDetail.techListUpdated"));
  };

  const assignBusTech = async (instId, tid) => {
    setBusy(true);
    try {
      await api.patch(`/installations/${instId}/assign`, { technician_id: tid });
      if (!assignedIds.includes(tid)) await api.patch(`/projects/${id}`, { technician_ids: [...assignedIds, tid] });
      toast.success(t("projectDetail.techAssigned"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveProductSerial = async (instId, val) => {
    if (!val || !val.trim()) return toast.error(t("projectDetail.enterSerial"));
    setBusy(true);
    try {
      await api.patch(`/installations/${instId}/product-serial`, { expected_product_serial: val.trim() });
      toast.success(t("projectDetail.serialSet"));
      setSerialEdits((s) => { const c = { ...s }; delete c[instId]; return c; });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReconcile = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/reconcile`, { parts_returned: parseInt(returned || "0") });
      toast.success(t("projectDetail.wastageComputed", { pct: data.wastage_percentage }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doClose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/close`);
      toast.success(t("projectDetail.invoiceToast", { invoice: data.invoice.invoice_id, adjustment: data.adjustment.adjustment_id }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/projects/${id}`);
      toast.success(t("projectDetail.archived"));
      navigate("/projects");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); setDelOpen(false); }
  };

  const updateBusRow = (i, field, val) => setNewBuses((rows) => rows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));
  const addBusRow = () => setNewBuses((rows) => [...rows, emptyBus()]);
  const removeBusRow = (i) => setNewBuses((rows) => rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows);

  const saveNewBuses = async () => {
    if (newBuses.some((b) => !b.bus_number.trim() || !b.vin.trim())) return toast.error(t("projectDetail.busNeedsNumberVin"));
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/buses`, { manifest: newBuses });
      toast.success(t("projectDetail.busesAdded", { count: data.added }));
      setAddOpen(false);
      setNewBuses([emptyBus()]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="project-detail">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> {t("projectDetail.backToProjects")}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-heading text-3xl font-black text-slate-900">{project.name || project.id}</h1>
            <StatusBadge status={project.status} />
          </div>
          <p className="mt-1 text-slate-500">{project.id} · {project.company?.name} · {project.pre_prep_required ? t("projectDetail.prePrepRequired") : t("projectDetail.noPrePrep")}</p>
        </div>
        {(isPM || isAdmin || can("projects.delete")) && (
          <div className="flex items-end gap-3">
            {(isPM || isAdmin) && (
              <div className="w-44">
                <Label className="overline text-slate-500">{t("projectDetail.status")}</Label>
                <Select value={project.status} onValueChange={(v) => setProjectField({ status: v })}>
                  <SelectTrigger data-testid="project-status-select" className="mt-1.5 border-slate-300 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {can("projects.delete") && (
              <Button data-testid="delete-project-btn" onClick={() => setDelOpen(true)} variant="outline" className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="h-4 w-4" /> {t("projectDetail.deleteProject")}
              </Button>
            )}
          </div>
        )}
      </div>

      <Card className="border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <Metric label={t("projectDetail.buses")} value={project.total_buses} />
          <Metric label={t("projectDetail.partsShipped")} value={project.parts_shipped} accent="text-blue-600" />
          <Metric label={t("projectDetail.installed")} value={installedCount} accent="text-emerald-600" />
          <Metric label={t("projectDetail.returned")} value={project.parts_returned} />
          <Metric label={t("projectDetail.wastage")} value={`${project.wastage_percentage}%`} accent="text-amber-600" />
          <Metric label={t("projectDetail.totalTime")} value={fmtDuration(totalTime)} accent="text-indigo-600" />
        </div>
      </Card>

      {project.reconciliation_error && (
        <Card className="border-orange-300 bg-orange-50 p-4 text-sm text-orange-700" data-testid="recon-error">
          {t("projectDetail.reconError", { error: project.reconciliation_error })}
        </Card>
      )}

      {isPM && (
        <Card className="border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Users className="h-5 w-5 text-blue-600" /> {t("projectDetail.projectTechnicians")}</h2>
          <div className="flex flex-wrap gap-2" data-testid="project-tech-list">
            {techs.map((t) => {
              const on = assignedIds.includes(t.id);
              return (
                <button key={t.id} data-testid={`proj-tech-${t.id}`} onClick={() => toggleProjectTech(t.id)} disabled={busy}
                  className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${on ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-blue-400"}`}>
                  {on && <Check className="h-3.5 w-3.5" />} {t.name}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {isAdmin && project.status === "Completed" && (
        <Card className="border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900"><Calculator className="h-5 w-5 text-amber-500" /> {t("projectDetail.reconciliationTitle")}</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="overline text-slate-500">{t("projectDetail.partsReturned")}</Label>
              <Input data-testid="parts-returned" type="number" value={returned} onChange={(e) => setReturned(e.target.value)} className="mt-1.5 w-40 border-slate-300 bg-white" />
            </div>
            <Button data-testid="reconcile-btn" onClick={doReconcile} disabled={busy} variant="outline" className="gap-2 border-slate-300"><Calculator className="h-4 w-4" /> {t("projectDetail.computeWastage")}</Button>
            <Button data-testid="close-project-btn" onClick={doClose} disabled={busy || !!project.zoho_invoice} className="gap-2 bg-emerald-600 hover:bg-emerald-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t("projectDetail.approveClose")}
            </Button>
          </div>
          {project.zoho_invoice && (
            <div className="mt-4 grid gap-2 rounded-md bg-slate-50 p-4 text-sm sm:grid-cols-2" data-testid="zoho-result">
              <div><span className="text-slate-500">{t("projectDetail.invoice")}:</span> <span className="text-slate-900">{project.zoho_invoice.invoice_id}</span> (${project.zoho_invoice.total})</div>
              <div><span className="text-slate-500">{t("projectDetail.inventoryAdj")}:</span> <span className="text-slate-900">{project.zoho_adjustment?.adjustment_id}</span> · {project.zoho_adjustment?.quantity_consumed} {t("projectDetail.units")}</div>
            </div>
          )}
        </Card>
      )}

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-slate-900">{t("projectDetail.busesCount", { count: filteredInstalls.length })}</h2>
          <div className="flex flex-wrap items-center gap-2">
            {can("projects.write") && (
              <Button size="sm" data-testid="add-buses-btn" onClick={() => { setNewBuses([emptyBus()]); setAddOpen(true); }} className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-500">
                <Plus className="h-4 w-4" /> {t("projectDetail.addBuses")}
              </Button>
            )}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="bus-search"
                value={busSearch}
                onChange={(e) => { setBusSearch(e.target.value); setPage(1); }}
                placeholder={t("projectDetail.searchBus")}
                className="h-9 w-64 border-slate-300 bg-white pl-9"
              />
            </div>
            <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="per-page-select" className="h-9 w-[130px] border-slate-300 bg-white text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{t("projectDetail.perPage", { count: n })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid gap-3">
          {pagedInstalls.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 py-10 text-center text-slate-400" data-testid="no-buses">{t("projectDetail.noBusesFound")}</div>
          )}
          {pagedInstalls.map((inst) => (
            <Card key={inst.id} data-testid={`install-${inst.id}`} className="flex flex-wrap items-center justify-between gap-3 border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-600"><Bus className="h-5 w-5" /></div>
                <div>
                  <div className="font-heading font-bold text-slate-900">{inst.bus_number || inst.expected_bus_serial || "—"}</div>
                  <div className="text-sm text-slate-500">
                    {inst.bus_type || inst.bus_variant || "—"}
                    {inst.manufacturer ? ` · ${inst.manufacturer}` : ""}
                    {inst.energy_source ? ` · ${inst.energy_source}` : ""}
                  </div>
                  <div className="text-xs text-slate-400">VIN: {inst.vin || inst.expected_bus_serial || "—"}</div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                    <Clock className="h-3 w-3" /> {fmtDuration(inst.elapsed_seconds)}
                    {inst.technician_name && <span>· {inst.technician_name}</span>}
                    {inst.timer_running && <span className="text-emerald-600">● running</span>}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-1.5 text-xs">
                  {inst.bus_verified ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-slate-300" />}
                  <span className="text-slate-500">{t("projectDetail.bus")}</span>
                  {inst.product_verified ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <XCircle className="h-4 w-4 text-slate-300" />}
                  <span className="text-slate-500">{t("projectDetail.product")}</span>
                </div>
                {isPM && (
                  <div className="w-40">
                    <Select value={inst.assigned_technician_id || ""} onValueChange={(v) => assignBusTech(inst.id, v)}>
                      <SelectTrigger data-testid={`assign-bus-${inst.id}`} className="h-9 border-slate-300 bg-white text-xs"><SelectValue placeholder={t("projectDetail.assignTech")} /></SelectTrigger>
                      <SelectContent>{techs.map((tech) => <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                {((inst.photos_before?.length || 0) + (inst.photos_after?.length || 0) > 0) && (
                  <Button size="sm" variant="ghost" data-testid={`vault-${inst.id}`} onClick={() => setVault(inst)} className="gap-1.5 text-slate-600"><ImageIcon className="h-4 w-4" /> {t("projectDetail.proofs")}</Button>
                )}
                <StatusBadge status={inst.status} />
                <Button size="sm" data-testid={`open-bus-${inst.id}`} onClick={() => navigate(`/installations/${inst.id}`)} className="gap-1.5 bg-blue-600 hover:bg-blue-500">
                  <Camera className="h-4 w-4" /> {isTech ? t("projectDetail.work") : t("projectDetail.open")}
                </Button>
              </div>
              {(isPM || isAdmin) && (
                <div className="flex w-full flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                  <Label className="overline shrink-0 text-slate-500">{t("projectDetail.productSerial")}</Label>
                  <Input
                    data-testid={`product-serial-input-${inst.id}`}
                    value={serialEdits[inst.id] !== undefined ? serialEdits[inst.id] : (inst.expected_product_serial || "")}
                    onChange={(e) => setSerialEdits((s) => ({ ...s, [inst.id]: e.target.value }))}
                    placeholder={t("projectDetail.setExpectedSerial")}
                    className="h-9 max-w-xs border-slate-300 bg-white"
                  />
                  <Button size="sm" data-testid={`save-serial-${inst.id}`} onClick={() => saveProductSerial(inst.id, serialEdits[inst.id] !== undefined ? serialEdits[inst.id] : (inst.expected_product_serial || ""))} disabled={busy} variant="outline" className="border-slate-300">{t("projectDetail.save")}</Button>
                  {inst.expected_product_serial && <span className="text-xs text-emerald-600">{t("projectDetail.current", { serial: inst.expected_product_serial })}</span>}
                </div>
              )}
            </Card>
          ))}
        </div>

        {filteredInstalls.length > perPage && (
          <div className="mt-4 flex items-center justify-between" data-testid="pagination">
            <span className="text-sm text-slate-500">{t("projectDetail.pageOf", { page: curPage, total: totalPages })}</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" data-testid="page-prev" disabled={curPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="gap-1 border-slate-300">
                <ChevronLeft className="h-4 w-4" /> {t("projectDetail.prev")}
              </Button>
              <Button size="sm" variant="outline" data-testid="page-next" disabled={curPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="gap-1 border-slate-300">
                {t("projectDetail.next")} <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!vault} onOpenChange={() => setVault(null)}>
        <DialogContent className="max-w-2xl border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("projectDetail.pictureVault")}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {[["before", "photos_before"], ["after", "photos_after"]].map(([which, key]) => {
              const list = vault?.[key] || [];
              const label = which === "before" ? t("projectDetail.before") : t("projectDetail.after");
              return (
                <div key={key}>
                  <div className="overline mb-1.5 text-slate-500">{label} ({list.length})</div>
                  <div className="grid grid-cols-2 gap-2">
                    {list.length ? list.map((p) => (
                      <img key={p} data-testid={`vault-img-${key}`} src={fileUrl(p)} alt={label} className="w-full rounded-md border border-slate-200" />
                    )) : <div className="col-span-2 flex h-32 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-400">{t("projectDetail.noPhoto")}</div>}
                  </div>
                </div>
              );
            })}
          </div>
          {vault?.supervisor_comments && <p className="mt-2 text-sm text-slate-500">{t("projectDetail.note", { note: vault.supervisor_comments })}</p>}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("projectDetail.addBusTitle")}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {newBuses.map((b, i) => (
              <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid={`new-bus-${i}`}>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1.3fr_auto]">
                  <Input data-testid={`new-bus-number-${i}`} placeholder={t("projectDetail.busNumber")} value={b.bus_number} onChange={(e) => updateBusRow(i, "bus_number", e.target.value)} className="border-slate-300 bg-white" />
                  <Input data-testid={`new-bus-vin-${i}`} placeholder={t("projectDetail.vin")} value={b.vin} onChange={(e) => updateBusRow(i, "vin", e.target.value)} className="border-slate-300 bg-white" />
                  <Button data-testid={`remove-new-bus-${i}`} onClick={() => removeBusRow(i)} size="icon" variant="ghost" disabled={newBuses.length === 1} className="justify-self-end text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Select value={b.bus_type} onValueChange={(v) => updateBusRow(i, "bus_type", v)}>
                    <SelectTrigger data-testid={`new-bus-type-${i}`} className="border-slate-300 bg-white"><SelectValue placeholder={t("projectDetail.busType")} /></SelectTrigger>
                    <SelectContent>{BUS_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input data-testid={`new-bus-manufacturer-${i}`} placeholder={t("projectDetail.manufacturer")} value={b.manufacturer} onChange={(e) => updateBusRow(i, "manufacturer", e.target.value)} className="border-slate-300 bg-white" />
                  <Select value={b.energy_source} onValueChange={(v) => updateBusRow(i, "energy_source", v)}>
                    <SelectTrigger data-testid={`new-bus-energy-${i}`} className="border-slate-300 bg-white"><SelectValue placeholder={t("projectDetail.energySource")} /></SelectTrigger>
                    <SelectContent>{ENERGY_SOURCES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
          <Button data-testid="add-another-bus" onClick={addBusRow} size="sm" variant="outline" className="gap-1.5 border-slate-300"><Plus className="h-4 w-4" /> {t("projectDetail.addAnother")}</Button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-slate-300">{t("projectDetail.cancel")}</Button>
            <Button data-testid="save-new-buses" onClick={saveNewBuses} disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("projectDetail.addBuses")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="border-slate-200 bg-white">
          <DialogHeader><DialogTitle className="font-heading text-slate-900">{t("projectDetail.deleteProject")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">{t("projectDetail.deleteProjectConfirm", { name: project.name || project.id })}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} className="border-slate-300">{t("projectDetail.cancel")}</Button>
            <Button data-testid="confirm-delete-project" onClick={doDelete} disabled={busy} className="gap-1.5 bg-red-600 hover:bg-red-500">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("projectDetail.confirmDelete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
